import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Input,
    Select,
    Option,
    Checkbox,
    Sheet,
    Typography,
    Card,
    CardContent,
    RadioGroup,
    Radio,
    Stack,
    Divider,
    FormControl,
    FormLabel,
} from '@mui/joy';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { QrPrefix } from '../utils/Utils';
import { PageStandard } from '../utils/PageStandard';
import { getQrCodes } from '../utils/ViewModel';
import { QrCodeCatalogEntry } from '../utils/Model';

type PaperLayout = 'A4-portrait' | 'A4-landscape';
type ImgFormat = 'png' | 'jpg';

const A4_MM = { width: 210, height: 297 }; // mm
const DPI = 300;
const MM_TO_PX = (mm: number) => Math.round((mm / 25.4) * DPI);

const defaultSizeMM = 50;
const defaultPaddingMM = 6;
const defaultBorderMM = 2;
const defaultGapMM = 5;

export function QrGenerator() {
    const [rawCode, setRawCode] = useState<string>('XXXXXX');
    const [qrCatalog, setQrCatalog] = useState<QrCodeCatalogEntry[]>([]);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [quantityPerPage, setQuantityPerPage] = useState<number>(12);
    const [showCropMarks, setShowCropMarks] = useState<boolean>(true);
    const [layout, setLayout] = useState<PaperLayout>('A4-portrait');
    const [qrSizeMM, setQrSizeMM] = useState<number>(defaultSizeMM);
    const [qrPaddingMM, setQrPaddingMM] = useState<number>(defaultPaddingMM);
    const [qrBorderMM, setQrBorderMM] = useState<number>(defaultBorderMM);
    const [gapMM, setGapMM] = useState<number>(defaultGapMM);
    const [imgBgColor, setImgBgColor] = useState<string>('#ffffff');
    const [imgFgColor, setImgFgColor] = useState<string>('#111111');
    const [imgFormat, setImgFormat] = useState<ImgFormat>('png');
    const [errorCorrection, setErrorCorrection] = useState<'L' | 'M' | 'Q' | 'H'>('M');
    const [showOuterBorder, setShowOuterBorder] = useState<boolean>(true);

    const previewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLoadingCatalog(true);
        getQrCodes().then(setQrCatalog).finally(() => setLoadingCatalog(false));
    }, []);

    const finalCode = useMemo(() => `${QrPrefix}${rawCode.trim()}`, [rawCode]); // stringa finale QR [web:82]

    const qrSizePX = MM_TO_PX(qrSizeMM);
    const qrPaddingPX = MM_TO_PX(qrPaddingMM);
    const qrBorderPX = MM_TO_PX(qrBorderMM);

    const a4Px = useMemo(() => {
        const isPortrait = layout === 'A4-portrait';
        const w = MM_TO_PX(isPortrait ? A4_MM.width : A4_MM.height);
        const h = MM_TO_PX(isPortrait ? A4_MM.height : A4_MM.width);
        return { w, h };
    }, [layout]); // A4 px [web:82]

    const cellInnerSizePX = qrSizePX + 2 * qrPaddingPX;
    const labelHeightPX = Math.ceil(0.24 * qrSizePX);
    const cellContentSizePX = cellInnerSizePX + labelHeightPX;

    // Se il bordo esterno è nascosto, non aggiungere lo spessore al box
    const effectiveBorderPX = showOuterBorder ? qrBorderPX : 0;
    const cellOuterSizePX = cellContentSizePX + 2 * effectiveBorderPX;

    const gapPX = MM_TO_PX(gapMM);

    const grid = useMemo(() => {
        const pitchX = cellOuterSizePX + gapPX;
        const pitchY = cellOuterSizePX + gapPX;
        const maxCols = Math.max(1, Math.floor((a4Px.w + gapPX) / pitchX));
        const maxRows = Math.max(1, Math.floor((a4Px.h + gapPX) / pitchY));
        let best = { cols: 1, rows: 1, diff: Infinity };
        for (let cols = 1; cols <= maxCols; cols++) {
            for (let rows = 1; rows <= maxRows; rows++) {
                const cap = cols * rows;
                const diff = Math.abs(cap - quantityPerPage);
                if (diff < best.diff) best = { cols, rows, diff };
            }
        }
        return { cols: best.cols, rows: best.rows, capacity: best.cols * best.rows };
    }, [a4Px.w, a4Px.h, cellOuterSizePX, gapPX, quantityPerPage]); // griglia [web:82]

    const cellStyle: React.CSSProperties = {
        width: `${cellOuterSizePX}px`,
        height: `${cellOuterSizePX}px`,
        boxSizing: 'border-box',
        border: showOuterBorder ? `${Math.max(1, Math.floor(qrBorderPX / 2))}px solid #333` : 'none',
        borderRadius: showOuterBorder ? Math.max(4, Math.floor(qrBorderPX)).toString() + 'px' : '0px',
        background: imgBgColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: `${effectiveBorderPX}px`,
        gap: `${Math.max(8, Math.floor(qrPaddingPX / 2))}px`,
    }; // stile cella con bordo opzionale [web:82]

    const renderQrCanvas = (value: string) => (
        <QRCodeCanvas
            id="qr-canvas"
            value={value}
            size={qrSizePX}
            bgColor={imgBgColor}
            fgColor={imgFgColor}
            level={errorCorrection}
            includeMargin={false}
            style={{ borderRadius: 8, imageRendering: 'pixelated' }}
        />
    ); // QR [web:82]

    const downloadPreview = async (format: ImgFormat) => {
        if (!previewRef.current) return;
        const canvas = await html2canvas(previewRef.current, {
            backgroundColor: imgBgColor,
            scale: 2,
        });
        const mime = format === 'png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(mime, 0.95);
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `qr_${rawCode || 'code'}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove(); // download immagine [web:82]
    };

    const drawCropMarks = (doc: jsPDF) => {
        if (!showCropMarks) return;
        const marginMM = 5;
        const markLenMM = 4;
        const lineW = 0.2;

        doc.setDrawColor(120);
        doc.setLineWidth(lineW);

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        doc.line(marginMM, marginMM, marginMM + markLenMM, marginMM);
        doc.line(marginMM, marginMM, marginMM, marginMM + markLenMM);
        doc.line(pageW - marginMM - markLenMM, marginMM, pageW - marginMM, marginMM);
        doc.line(pageW - marginMM, marginMM, pageW - marginMM, marginMM + markLenMM);
        doc.line(marginMM, pageH - marginMM, marginMM + markLenMM, pageH - marginMM);
        doc.line(marginMM, pageH - marginMM - markLenMM, marginMM, pageH - marginMM);
        doc.line(pageW - marginMM - markLenMM, pageH - marginMM, pageW - marginMM, pageH - marginMM);
        doc.line(pageW - marginMM, pageH - marginMM - markLenMM, pageW - marginMM, pageH - marginMM);
    }; // crop marks [web:73]

    const generatePdf = async () => {
        const orientation = layout === 'A4-portrait' ? 'p' : 'l';
        const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

        // Dimensioni cella in mm coerenti con il bordo opzionale
        const cellOuterSizeMM = (cellOuterSizePX / DPI) * 25.4;
        const gap = Math.max(1, gapMM);
        const marginMM = 10;

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        const pitchX = cellOuterSizeMM + gap;
        const pitchY = cellOuterSizeMM + gap;

        const cols = Math.max(1, Math.floor((pageW - 2 * marginMM + gap) / pitchX));
        const rows = Math.max(1, Math.floor((pageH - 2 * marginMM + gap) / pitchY));
        const totalPerPage = cols * rows;

        drawCropMarks(doc);

        let printed = 0;
        const total = Math.max(totalPerPage, Math.min(totalPerPage, quantityPerPage));

        // Raster dalla preview attuale: riflette lo stato del bordo
        const cellCanvas = await html2canvas(previewRef.current as HTMLDivElement, {
            backgroundColor: imgBgColor,
            scale: 2,
        });
        const imgData = cellCanvas.toDataURL('image/png', 0.98);

        for (let r = 0; r < rows && printed < total; r++) {
            for (let c = 0; c < cols && printed < total; c++) {
                const xMM = marginMM + c * pitchX;
                const yMM = marginMM + r * pitchY;
                doc.addImage(imgData, 'PNG', xMM, yMM, cellOuterSizeMM, cellOuterSizeMM); // layout con gap [web:73]
                printed++;
            }
        }

        doc.save(`qr_sheet_${rawCode || 'codici'}.pdf`);
    };

    type EC = 'L' | 'M' | 'Q' | 'H';
    const handleECChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value as string;
        if (v === 'L' || v === 'M' || v === 'Q' || v === 'H') setErrorCorrection(v);
        else setErrorCorrection('M');
    }; // Joy RadioGroup [web:73]

    return (
        <PageStandard title="Qr Code Generator" sidebarVisible={true}>
            <Stack spacing={2}>
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 10 }}>
                    <Typography level="h4">Impostazioni</Typography>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                        <FormControl size="sm">
                            <FormLabel>QR dal catalogo</FormLabel>
                            <Select
                                placeholder={loadingCatalog ? 'Caricamento…' : 'Seleziona un QR fisso'}
                                value={null}
                                onChange={(_, v) => v && setRawCode(v as string)}
                                sx={{ minWidth: 220 }}
                            >
                                {qrCatalog.map((q) => (
                                    <Option key={q.id} value={q.code}>
                                        {(q.label || q.code)}{q.lat == null ? ' · senza posizione' : ''}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Codice</FormLabel>
                            <Input
                                value={rawCode}
                                onChange={(e) => setRawCode(e.target.value.toUpperCase())}
                                placeholder="Codice tipo XXXXXX"
                                slotProps={{ input: { maxLength: 32 } }}
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Formato pagina</FormLabel>
                            <Select value={layout} onChange={(_, v) => v && setLayout(v)}>
                                <Option value="A4-portrait">A4 verticale</Option>
                                <Option value="A4-landscape">A4 orizzontale</Option>
                            </Select>
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Qtà per foglio</FormLabel>
                            <Input
                                type="number"
                                value={quantityPerPage}
                                onChange={(e) => setQuantityPerPage(Math.max(1, parseInt(e.target.value || '1', 10)))}
                                placeholder="Qtà per foglio"
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Bordi di ritaglio</FormLabel>
                            <Checkbox
                                label="Mostra"
                                checked={showCropMarks}
                                onChange={(e) => setShowCropMarks(e.target.checked)}
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Bordo esterno</FormLabel>
                            <Checkbox
                                label="Mostra"
                                checked={showOuterBorder}
                                onChange={(e) => setShowOuterBorder(e.target.checked)}
                            />
                        </FormControl>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Typography level="title-lg">Stile QR</Typography>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                        <FormControl size="sm">
                            <FormLabel>Lato QR (mm)</FormLabel>
                            <Input
                                type="number"
                                value={qrSizeMM}
                                onChange={(e) => setQrSizeMM(Math.max(20, Math.min(120, parseInt(e.target.value || '50', 10))))}
                                placeholder="Lato QR (mm)"
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Padding (mm)</FormLabel>
                            <Input
                                type="number"
                                value={qrPaddingMM}
                                onChange={(e) => setQrPaddingMM(Math.max(2, Math.min(20, parseInt(e.target.value || '6', 10))))}
                                placeholder="Padding (mm)"
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Bordo (mm)</FormLabel>
                            <Input
                                type="number"
                                value={qrBorderMM}
                                onChange={(e) => setQrBorderMM(Math.max(0, Math.min(8, parseInt(e.target.value || '2', 10))))}
                                placeholder="Bordo (mm)"
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Gap tra codici (mm)</FormLabel>
                            <Input
                                type="number"
                                value={gapMM}
                                onChange={(e) => setGapMM(Math.max(0, Math.min(30, parseInt(e.target.value || '5', 10))))}
                                placeholder="Distanza per ritaglio"
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Colore sfondo</FormLabel>
                            <Input
                                type="color"
                                value={imgBgColor}
                                onChange={(e) => setImgBgColor(e.target.value)}
                                sx={{ width: 64, p: 0 }}
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Colore QR</FormLabel>
                            <Input
                                type="color"
                                value={imgFgColor}
                                onChange={(e) => setImgFgColor(e.target.value)}
                                sx={{ width: 64, p: 0 }}
                            />
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Correzione errore</FormLabel>
                            <RadioGroup orientation="horizontal" value={errorCorrection} onChange={handleECChange}>
                                <Radio value="L" label="EC L" />
                                <Radio value="M" label="EC M" />
                                <Radio value="Q" label="EC Q" />
                                <Radio value="H" label="EC H" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl size="sm">
                            <FormLabel>Formato immagine</FormLabel>
                            <Select value={imgFormat} onChange={(_, v) => v && setImgFormat(v)}>
                                <Option value="png">PNG</Option>
                                <Option value="jpg">JPG</Option>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" spacing={2}>
                        <Button color="neutral" variant="outlined" onClick={() => downloadPreview('png')}>
                            Scarica PNG
                        </Button>
                        <Button color="neutral" variant="outlined" onClick={() => downloadPreview('jpg')}>
                            Scarica JPG
                        </Button>
                        <Button color="primary" onClick={generatePdf}>
                            Genera PDF
                        </Button>
                    </Stack>
                </Sheet>

                <Card variant="soft">
                    <CardContent>
                        <Typography level="title-lg">Anteprima</Typography>
                        <Typography level="body-sm" sx={{ mb: 1 }}>
                            Stringa QR: {finalCode}
                        </Typography>

                        <div style={{ width: Math.max(260, cellOuterSizePX), maxWidth: '100%' }}>
                            <div
                                ref={previewRef}
                                style={{
                                    aspectRatio: '1 / 1',
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    padding: `${effectiveBorderPX}px`,
                                    border: showOuterBorder
                                        ? `${Math.max(1, Math.floor(qrBorderPX / 2))}px solid #333`
                                        : 'none',
                                    borderRadius: showOuterBorder
                                        ? Math.max(6, Math.floor(qrBorderPX)).toString() + 'px'
                                        : '0px',
                                    background: imgBgColor,
                                    gap: `${Math.max(8, Math.floor(qrPaddingPX / 2))}px`,
                                    overflow: 'hidden',
                                }}
                            >
                                {renderQrCanvas(finalCode)}
                                <Typography
                                    level="body-lg"
                                    sx={{ fontWeight: 600, color: '#111', lineHeight: 1.2, textAlign: 'center', px: 1 }}
                                >
                                    Codice: {rawCode || 'XXXXXX'}
                                </Typography>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </Stack>
        </PageStandard>
    );
}
